<template>
  <div id="positions-by-summary">
    <v-card class="pl-5 pt-1 pb-6 my-0 elevation-0">
      <v-card-title class="px-1" :class="{ 'mb-4': !isLg }">
        <h1 class="card-title">
          <template v-if="bySummary !== 'byTicker'">
            {{ $t(`views.dashboardDetail.summary.positionsBySummary.${bySummary}.title`) }}
          </template>
          <template v-else>
            {{
              type
                ? $t(`views.dashboardDetail.summary.positionsBySummary.${bySummary}.${type}.title`)
                : $t(`views.dashboardDetail.summary.positionsBySummary.${bySummary}.all.title`)
            }}
          </template>
        </h1>

        <v-spacer />

        <div class="mt-1 mr-4">
          <v-btn-toggle v-model="showTotalValue" mandatory class="show-total-value-buttons">
            <v-btn
              small
              color="softBackgroundGrey"
              :class="{ 'black--text': !$vuetify.theme.dark, 'white--text': $vuetify.theme.dark }"
              :value="true"
            >
              {{ $t(`views.dashboardDetail.summary.positionsBySummary.totalValue`) }}
            </v-btn>
            <v-btn
              small
              color="softBackgroundGrey"
              :class="{ 'black--text': !$vuetify.theme.dark, 'white--text': $vuetify.theme.dark }"
              :value="false"
            >
              {{ $t(`views.dashboardDetail.summary.positionsBySummary.totalCost`) }}
            </v-btn>
          </v-btn-toggle>
        </div>
      </v-card-title>
      <v-card-text class="mt-0 pa-0">
        <BarLoading v-if="!loadedData" />
        <v-row v-else class="align-start">
          <v-col cols="12" md="12" lg="7" xl="8" class="py-0">
            <div class="mt-0 pt-0">
              <v-data-table
                id="positions-by-summary-table"
                :headers="headers"
                :items="summary"
                class="elevation-0 left-sticky-table pt-1"
                mobile-breakpoint="0"
                :loading="!loadedData"
                :items-per-page="10"
                :page.sync="currentPage"
                :item-class="itemRowBackground"
                :dense="user.preferences.denseTables"
              >
                <template v-if="bySummary !== 'byTags'" slot="body.prepend">
                  <tr
                    v-if="totalRow"
                    :class="{
                      'row-recreating': somePendingRecreatePosition,
                      'row-total': !somePendingRecreatePosition,
                    }"
                  >
                    <td>
                      <div class="items-flex align-start">
                        <span class="table-id">TOTAL</span>
                        <div v-if="somePendingRecreatePosition" class="ml-3">
                          <TableCoreRecreating
                            :text="$t('views.common.tableCoreRecreating.textTotal')"
                          />
                        </div>
                      </div>
                    </td>
                    <td class="text-right">
                      <currency
                        :value="totalRow.totalValueBaseCurrency"
                        :currency="user.baseCurrency"
                      />
                    </td>
                    <td class="text-right">
                      {{ round(totalRow.weightTotalValue).toLocaleString() }}%
                    </td>
                    <td v-if="['bySector'].indexOf(bySummary) > -1" class="text-right">
                      <span />
                    </td>
                    <td class="text-right">
                      <currency
                        :value="totalRow.totalCostWithComissionBaseCurrency"
                        :currency="user.baseCurrency"
                      />
                    </td>
                    <td class="text-right">
                      {{ round(totalRow.weightTotalCost).toLocaleString() }}%
                    </td>

                    <td class="text-right row-total-currency">
                      <PositionTablePL :position="totalRow" type="net" />
                    </td>
                    <td class="text-right row-total-currency">
                      <PositionTablePL :position="totalRow" type="percent" />
                    </td>
                    <td class="text-right">
                      <currency
                        :value="totalRow.totalDividendWithReturnBaseCurrency"
                        :currency="user.baseCurrency"
                      />
                    </td>
                  </tr>
                </template>

                <template v-slot:item._id="{ item }">
                  <div class="table-id items-flex align-start">
                    <template v-if="bySummary === 'byTicker'">
                      <TableTicker :position="{ ticker: item._id, ...item }" notLinkToDetail />
                    </template>
                    <template v-else-if="bySummary === 'bySector'">
                      <span v-if="item._id">{{ item._id }}</span>
                      <span v-else-if="isNoSector(item)" class="value-italic">
                        {{
                          $t('views.dashboardDetail.summary.positionsBySummary.bySector.noSector')
                        }}
                      </span>
                    </template>
                    <template v-else-if="bySummary === 'byType'">
                      <span>
                        {{ $t(`common.positionTypes.${item._id}`) }}
                      </span>
                    </template>
                    <template
                      v-else-if="bySummary === 'byCountry' || bySummary === 'byExposureCountry'"
                    >
                      <span>
                        <span v-if="isNoCountry(item)" class="value-italic">
                          {{ $t(`views.dashboardDetail.summary.positionsByCountry.noCountry`) }}
                        </span>
                        <span v-else>
                          {{
                            $te(`common.countries.${item._id}`)
                              ? $t(`common.countries.${item._id}`)
                              : item._id
                          }}
                        </span>
                      </span>
                    </template>
                    <template v-else-if="bySummary === 'byExchange'">
                      <span>
                        <span v-if="isNoExchange(item)" class="value-italic">
                          {{
                            $t(
                              `views.dashboardDetail.summary.positionsBySummary.byExchange.noExchange`
                            )
                          }}
                        </span>
                        <span v-else>
                          {{
                            $te(`common.exchanges.${item._id.country}.${item._id.exchange}`)
                              ? $t(`common.exchanges.${item._id.country}.${item._id.exchange}`)
                              : item._id.exchange
                          }}
                        </span>
                      </span>
                    </template>
                    <template v-else-if="bySummary === 'byTags'">
                      <span v-if="!item._id" class="value-italic">
                        {{ $t('views.dashboardDetail.summary.positionsByTags.noTags') }}
                      </span>
                      <span v-else>{{ item._id }}</span>
                    </template>
                    <template v-else-if="bySummary === 'byManager'">
                      <span v-if="item._id">{{ item._id }}</span>
                      <span v-else-if="isNoManager(item)" class="value-italic">
                        {{
                          $t('views.dashboardDetail.summary.positionsBySummary.byManager.noManager')
                        }}
                      </span>
                    </template>
                    <template v-else>
                      {{ item._id }}
                    </template>

                    <template
                      v-if="
                        (bySummary === 'byTicker' &&
                          item.totalCostWithComissionBaseCurrency === null) ||
                        (bySummary !== 'byTicker' && someNullExchangeRate(item))
                      "
                    >
                      <div class="ml-3">
                        <WarningIcon
                          :text="
                            $t(
                              `views.dashboardDetail.summary.positionsBySummary.${bySummary}.warning`
                            )
                          "
                        />
                      </div>
                    </template>
                  </div>
                </template>

                <template v-slot:item.totalValueBaseCurrency="{ item }">
                  <div
                    v-if="item.totalValueBaseCurrency"
                    :class="{ 'value-strong': showTotalValue }"
                  >
                    <currency :value="item.totalValueBaseCurrency" :currency="user.baseCurrency" />
                  </div>
                  <span v-else></span>
                </template>
                <template v-if="bySummary !== 'byTags'" v-slot:item.weightTotalValue="{ item }">
                  <div :class="{ 'value-strong': showTotalValue }">
                    {{ round(item.weightTotalValue).toLocaleString() }}%
                  </div>
                </template>

                <!-- <template
                v-if="['byTicker', 'byCurrency'].indexOf(bySummary) > -1"
                v-slot:item.totalCost="{ item }"
              >
                <currency
                  v-if="item._id"
                  :value="item.totalCost"
                  :currency="bySummary === 'byCurrency' ? item._id : item.currency"
                />
                <span v-else></span>
              </template> -->

                <template v-slot:item.totalCostWithComissionBaseCurrency="{ item }">
                  <div :class="{ 'value-strong': !showTotalValue }">
                    <currency
                      v-if="item.totalCostWithComissionBaseCurrency"
                      :value="item.totalCostWithComissionBaseCurrency"
                      :currency="user.baseCurrency"
                    />
                    <span v-else></span>
                  </div>
                </template>
                <template v-if="bySummary !== 'byTags'" v-slot:item.weightTotalCost="{ item }">
                  <div :class="{ 'value-strong': !showTotalValue }">
                    {{ round(item.weightTotalCost).toLocaleString() }}%
                  </div>
                </template>

                <template v-slot:item.plBaseCurrency="{ item }">
                  <PositionTablePL :position="item" type="net" />
                </template>
                <template v-slot:item.plBaseCurrencyPercent="{ item }">
                  <PositionTablePL :position="item" type="percent" />
                </template>
                <template v-slot:item.totalDividendWithReturnBaseCurrency="{ item }">
                  <currency
                    :value="item.totalDividendWithReturnBaseCurrency || 0"
                    :currency="user.baseCurrency"
                  />
                </template>
              </v-data-table>
            </div>
          </v-col>
          <v-col cols="12" md="12" lg="5" xl="4" class="pt-6">
            <DonutChart
              v-if="loadedData"
              ref="donutChart"
              :chartData="donutChartData"
              :chartOptions="donutChartOptions"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { get, map, orderBy } from 'lodash'
import { mapState } from 'vuex'
import { getPositionSummary } from '@/lib/api.js'
import { round } from '@/lib/utils/math.js'
import { DONUT_PALETTE, DARK_DEFAULT_PALETTE } from '@/lib/colors.js'
import { getCurrencyValueAsString, getCurrencySymbol } from '@/lib/utils/currencies.js'
import DonutChart from '@/views/components/core/chartjs/DonutChart'
import BarLoading from '@/views/components/core/BarLoading'
import Currency from '@/views/components/common/Currency'
import WarningIcon from '@/views/components/common/WarningIcon'
import TableCoreRecreating from '@/views/components/common/positions/TableCoreRecreating'
import PositionTablePL from '@/views/components/common/positions/PositionTablePL'
import TableTicker from '@/views/components/common/TableTicker'

export default {
  components: {
    TableTicker,
    DonutChart,
    BarLoading,
    Currency,
    WarningIcon,
    TableCoreRecreating,
    PositionTablePL,
  },
  props: {
    bySummary: {
      type: String,
      required: true,
    },
    somePendingRecreatePosition: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    type: null,
    loadedData: false,
    totalRow: null,
    showTotalValue: true,
    loadedSummary: [],
    summary: [],
    currentPage: null,
    donutChartData: {
      labels: null,
      datasets: null,
    },
  }),
  computed: {
    ...mapState(['user', 'hideAmounts']),
    isLg() {
      return this.$vuetify.breakpoint.lgAndUp
    },
    donutChartOptions() {
      const context = this
      const showDistributionLegendPreference = get(
        this.user,
        'preferences.showDistributionLegend',
        false
      )
      return {
        maintainAspectRatio: false,
        legend: {
          display: showDistributionLegendPreference,
        },
        tooltips: {
          callbacks: {
            label: function (tooltipItem, data) {
              let label = data.datasets[tooltipItem.datasetIndex].label || ''
              if (label) label += ': '

              const value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index]
              label += getCurrencyValueAsString({
                value,
                currency: context.user.baseCurrency,
                hide: context.hideAmounts,
              })

              let percent =
                data.datasets[tooltipItem.datasetIndex].additionalData.percents[tooltipItem.index]
              if (percent) {
                percent = getCurrencyValueAsString({
                  value: percent,
                })
                return `${label} (${percent}%)`
              }
              return `${label}`
            },
            title: function (tooltipItems, data) {
              return data.labels[tooltipItems[0].index] || ''
            },
          },
        },
      }
    },
    headers() {
      const headers = [{ text: this.getHeaderId(), value: '_id', divider: true }]

      headers.push({
        text: `${this.$t('views.stocks.stockTable.totalValue')} (${getCurrencySymbol(
          this.user.baseCurrency
        )})`,
        value: 'totalValueBaseCurrency',
        align: 'right',
      })

      if (this.bySummary !== 'byTags') {
        headers.push({
          text: `${this.$t('views.stocks.stockTable.totalValue')} (${getCurrencySymbol(
            this.user.baseCurrency
          )}) %`,
          value: 'weightTotalValue',
          align: 'right',
        })
      }

      if (this.bySummary === 'bySector') {
        headers.push({
          text: this.$t('views.dashboardDetail.summary.positionsBySummary.bySector.numPositions'),
          value: 'numPositions',
          align: 'right',
        })
      }

      headers.push({
        text: `${this.$t('views.stocks.stockTable.totalCostWithComission')} (${getCurrencySymbol(
          this.user.baseCurrency
        )})`,
        value: 'totalCostWithComissionBaseCurrency',
        align: 'right',
      })

      if (this.bySummary !== 'byTags') {
        headers.push({
          text: `${this.$t('views.stocks.stockTable.totalCostWithComission')} (${getCurrencySymbol(
            this.user.baseCurrency
          )}) %`,
          value: 'weightTotalCost',
          align: 'right',
        })
      }

      headers.push({
        text: `${this.$t('views.stocks.stockTable.pl')} (${getCurrencySymbol(
          this.user.baseCurrency
        )})`,
        value: 'plBaseCurrency',
        align: 'right',
      })
      headers.push({
        text: `${this.$t('views.stocks.stockTable.plPercent')}`,
        value: 'plBaseCurrencyPercent',
        align: 'right',
      })
      headers.push({
        text: `${this.$t('views.stocks.stockTable.totalDividendWithReturn')} (${getCurrencySymbol(
          this.user.baseCurrency
        )})`,
        value: 'totalDividendWithReturnBaseCurrency',
        align: 'right',
      })
      return headers
    },
  },
  watch: {
    showTotalValue() {
      this.setSummary()
    },
    '$vuetify.theme.dark'() {
      this.setSummary()
    },
  },
  methods: {
    round,
    getHeaderId() {
      if (this.bySummary === 'byTicker') {
        return this.type
          ? this.$t(`views.dashboardDetail.summary.positionsByTicker.${this.type}.idHeaderText`)
          : ''
      }
      if (this.bySummary === 'byType') return this.$t('common.type')
      if (this.bySummary === 'bySector') return this.$t('common.sector')
      if (this.bySummary === 'byCurrency') return this.$t('views.stocks.stockTable.currency')
      if (this.bySummary === 'byCountry') return this.$t('views.stocks.stockTable.country')
      if (this.bySummary === 'byExchange') return this.$t('views.stocks.stockTable.exchange')
      if (this.bySummary === 'byExposureCountry')
        return this.$t('views.stocks.stockTable.exposureCountry')
      if (this.bySummary === 'byTags') return this.$t('common.tag')
      if (this.bySummary === 'byManager') return this.$t('common.manager')
      return ''
    },
    isNoSector(item) {
      return !item._id && item.numPositions !== undefined
    },
    isNoCountry(item) {
      return !item._id
    },
    isNoExchange(item) {
      return Object.keys(item._id).length === 0
    },
    isNoManager(item) {
      return !item._id
    },
    someNullExchangeRate(item) {
      return (
        (item.exchangeRates && item.exchangeRates.some((elem) => elem === null)) ||
        (item.totalCostsWithComissionBaseCurrency &&
          item.totalCostsWithComissionBaseCurrency.some((elem) => elem === null)) ||
        (item.totalReceivedsWithComissionBaseCurrency &&
          item.totalReceivedsWithComissionBaseCurrency.some((elem) => elem === null))
      )
    },
    itemRowBackground(item) {
      if (
        (this.bySummary === 'byTicker' && item.totalCostWithComissionBaseCurrency === null) ||
        (this.bySummary !== 'byTicker' && this.someNullExchangeRate(item))
      )
        return 'row-advise'
    },
    setChartData() {
      const MAX_ELEMENTS_BEFORE_REMAINING = 20

      const weightType = this.showTotalValue ? 'weightTotalValue' : 'weightTotalCost'
      const totalType = this.showTotalValue
        ? 'totalValueBaseCurrency'
        : 'totalCostWithComissionBaseCurrency'

      let summary = this.summary.filter((elem) => elem[totalType])

      if (this.bySummary !== 'byTags') {
        // REMAINING
        if (this.summary.length > MAX_ELEMENTS_BEFORE_REMAINING) {
          summary = this.summary.slice(0, MAX_ELEMENTS_BEFORE_REMAINING)

          let remainingTotal = this.totalRow[totalType]
          let remainingWeight = 100
          summary.forEach((item) => {
            remainingTotal -= item[totalType]
            remainingWeight -= item[weightType]
          })

          const remaining = {}
          remaining[totalType] = remainingTotal
          remaining[weightType] = remainingWeight
          remaining._id = 'remaining'
          summary.push(remaining)
        }
        // ---
      }

      this.donutChartData.datasets = [
        {
          label: this.showTotalValue
            ? this.$t(`views.dashboardDetail.summary.positionsBySummary.totalValue`)
            : this.$t(`views.dashboardDetail.summary.positionsBySummary.totalCost`),
          data: map(summary, (item) => {
            return item[totalType]
          }),
          backgroundColor: Object.values(DONUT_PALETTE),
          borderColor: this.$vuetify.theme.dark
            ? DARK_DEFAULT_PALETTE.dark_backgroundLighterBlack
            : 'white',
          additionalData: {
            percents: map(summary, (item) => {
              return item[weightType]
            }),
          },
        },
      ]

      const labels = map(summary, (item) => {
        // REMAINING
        if (item._id === 'remaining')
          return this.$t('views.dashboardDetail.summary.positionsBySummary.remaining')
        // ---

        if (this.bySummary === 'byTicker') {
          if (['fund'].indexOf(item.type) > -1 && item.name) return item.name
          return item._id
        } else if (this.bySummary === 'bySector') {
          if (this.isNoSector(item))
            return this.$t('views.dashboardDetail.summary.positionsBySummary.bySector.noSector')
          else return item._id
        } else if (this.bySummary === 'byType') {
          return this.$t(`common.positionTypes.${item._id}`)
        } else if (this.bySummary === 'byCountry' || this.bySummary === 'byExposureCountry') {
          if (this.isNoCountry(item))
            return this.$t('views.dashboardDetail.summary.positionsByCountry.noCountry')
          else
            return this.$te(`common.countries.${item._id}`)
              ? this.$t(`common.countries.${item._id}`)
              : item._id
        } else if (this.bySummary === 'byExchange') {
          if (this.isNoExchange(item))
            return this.$t('views.dashboardDetail.summary.positionsBySummary.byExchange.noExchange')
          else
            return this.$te(`common.exchanges.${item._id.country}.${item._id.exchange}`)
              ? this.$t(`common.exchanges.${item._id.country}.${item._id.exchange}`)
              : item._id.exchange
        } else if (this.bySummary === 'byTags') {
          if (!item._id) return this.$t('views.dashboardDetail.summary.positionsByTags.noTags')
          else return item._id
        } else if (this.bySummary === 'byManager') {
          if (this.isNoManager(item))
            return this.$t('views.dashboardDetail.summary.positionsBySummary.byManager.noManager')
          else return item._id
        }
        return item._id
      })
      this.donutChartData.labels = labels

      if (this.$refs.donutChart) this.$refs.donutChart.render()
    },
    setSummary() {
      if (this.showTotalValue) {
        this.summary = orderBy(
          this.loadedSummary,
          ['totalValueBaseCurrency', 'ticker'],
          ['desc', 'asc']
        )
      } else {
        this.summary = JSON.parse(JSON.stringify(this.loadedSummary))
      }
      this.currentPage = 1
      this.setChartData()
    },
    async loadSummary({ filters, type }) {
      this.type = type

      this.loadedData = false
      try {
        const type =
          this.type === 'all' ? undefined : this.type === 'allNeCash' ? { $ne: 'cash' } : this.type
        const summary = await getPositionSummary({
          group: this.bySummary,
          filter: {
            type,
            positionNumber: { $gte: 0 },
            ...filters,
          },
        })
        if (this.bySummary !== 'byTags') this.totalRow = summary.data.shift()
        this.loadedSummary = summary.data
        this.setSummary()
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
      this.loadedData = true
    },
  },
}
</script>

<style lang="scss">
#positions-by-summary {
  height: 100%;
  .v-card {
    height: 100%;
  }

  .show-total-value-buttons {
    .v-btn__content {
      text-transform: none;
    }
  }

  #positions-by-summary-table {
    .items-flex {
      display: flex;
      justify-content: flex-end;
      &.align-start {
        justify-content: flex-start;
      }
    }
    .value-strong {
      font-weight: 400;
    }
    .value-italic {
      font-style: italic;
    }
    .table-id {
      font-weight: 500;
    }

    .row-total-currency {
      #currency {
        font-size: 16px;
      }
    }
  }
}
</style>
